import React, { useEffect, useState } from 'react';
import { api, gmailConnectUrl, myobConnectUrl } from '../lib/api';
import { T, sx } from '../lib/tokens';
import { Wordmark } from '../components/ui/Wordmark';
import { Button } from '../components/ui/Button';
import { FieldSelect } from '../components/ui/FieldInput';
import { FieldInput } from '../components/ui/FieldInput';
import { Arrow, Check, Plus, X, Sparkle, XeroX } from '../components/ui/Icons';

interface Props {
  onComplete: () => void;
}

type Provider = 'xero' | 'myob';

// Normalised shape so Xero's { AccountID, Code, Name } and MYOB's { UID, DisplayID, Name }
// collapse into a single option type.
interface AccountOption {
  id: string;    // AccountID | UID — used as the picker value
  code: string;  // Code | DisplayID — used for the stored default (Xero account_code)
  name: string;
}

interface TaxCodeOption {
  id: string;    // MYOB UID
  code: string;  // e.g. "GST"
  name: string;
  rate: number;
}

const TOTAL_STEPS = 7;

export function Onboarding({ onComplete }: Props) {
  const [step, setStep] = useState(0);
  const [provider, setProvider] = useState<Provider>('xero');
  const [myobConfigured, setMyobConfigured] = useState(false);
  const [myobConnected, setMyobConnected] = useState(false);

  const [bankAccounts, setBankAccounts] = useState<AccountOption[]>([]);
  const [expenseAccounts, setExpenseAccounts] = useState<AccountOption[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCodeOption[]>([]);

  const [selectedBank, setSelectedBank] = useState('');
  const [selectedExpense, setSelectedExpense] = useState('');
  const [selectedTax, setSelectedTax] = useState('');

  const [aliases, setAliases] = useState<string[]>([]);
  const [aliasInput, setAliasInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gmail, setGmail] = useState<null | { connected: boolean; email?: string }>(null);
  const [settingsSaved, setSettingsSaved] = useState(false);

  async function refreshGmail() {
    try {
      const s = await api.gmailStatus();
      setGmail(s);
    } catch { /* ignore */ }
  }

  async function loadAccountsForProvider(next: Provider) {
    try {
      if (next === 'myob') {
        const [b, a, t] = await Promise.all([
          api.myobBankAccounts(),
          api.myobAccounts(),
          api.myobTaxCodes(),
        ]);
        setBankAccounts(b.accounts.map((x) => ({ id: x.UID, code: x.DisplayID, name: `${x.Name} (${x.DisplayID})` })));
        setExpenseAccounts(a.accounts.map((x) => ({ id: x.UID, code: x.DisplayID, name: `${x.DisplayID} – ${x.Name}` })));
        setTaxCodes(t.codes.map((x) => ({ id: x.UID, code: x.Code, name: `${x.Code} – ${x.Description}`, rate: x.Rate })));
      } else {
        const [b, a] = await Promise.all([api.xeroBankAccounts(), api.xeroAccounts()]);
        setBankAccounts(b.accounts.map((x) => ({ id: x.AccountID, code: x.Code, name: `${x.Name} (${x.Code})` })));
        setExpenseAccounts(
          a.accounts
            .filter((acc) => acc.Type === 'EXPENSE' || acc.Type === 'DIRECTCOSTS')
            .map((x) => ({ id: x.AccountID, code: x.Code, name: `${x.Code} – ${x.Name}` }))
        );
        setTaxCodes([]);
      }
    } catch (err) {
      console.warn('Could not load accounts', err);
    }
  }

  useEffect(() => {
    (async () => {
      // Detect which provider is active. If the user has connected MYOB (via
      // the picker + OAuth round-trip), switch to MYOB mode. Otherwise stay
      // on the Xero default from signup.
      let initialProvider: Provider = 'xero';
      try {
        const s = await api.myobStatus();
        setMyobConfigured(s.configured);
        setMyobConnected(s.connected);
        if (s.connected) initialProvider = 'myob';
      } catch { /* ignore — MYOB is optional */ }

      setProvider(initialProvider);
      await loadAccountsForProvider(initialProvider);
      await refreshGmail();

      // Resume at the right step after an OAuth round-trip (Gmail or MYOB).
      const resume = sessionStorage.getItem('ra_onboarding_resume');
      if (resume === 'gmail') {
        setSettingsSaved(true);
        setStep(5);
      } else if (resume === 'myob') {
        // Back from MYOB OAuth — drop them on the bank step with the new provider loaded.
        setStep(2);
      }
      setLoading(false);
    })();

    const onFocus = () => { void refreshGmail(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function switchToMyob() {
    setSaving(true);
    try {
      // Disconnect Xero first so we don't have two active connections on record.
      await api.xeroDisconnect().catch(() => {});
      sessionStorage.setItem('ra_onboarding_resume', 'myob');
      window.location.href = myobConnectUrl();
    } catch (err) {
      setSaving(false);
      console.warn('switch to MYOB failed', err);
    }
  }

  function addAlias() {
    const email = aliasInput.trim().toLowerCase();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    if (aliases.includes(email)) return;
    setAliases([...aliases, email]);
    setAliasInput('');
  }

  async function persistSettings() {
    if (settingsSaved) return;
    if (provider === 'myob') {
      await api.saveMyobDefaults({
        default_bank_account_uid: selectedBank,   // MYOB UID
        default_account_uid: selectedExpense,     // MYOB expense UID
        default_tax_code_uid: selectedTax,        // MYOB tax UID
      });
    } else {
      await api.saveXeroDefaults({
        default_bank_account_id: selectedBank,    // Xero AccountID
        default_account_code: selectedExpense,    // Xero Code (string)
      });
    }
    for (const email of aliases) {
      try {
        await api.addEmailAlias(email);
      } catch (err) {
        console.warn('Could not add alias', email, err);
      }
    }
    setSettingsSaved(true);
  }

  async function gotoGmailStep() {
    setSaving(true);
    try { await persistSettings(); } catch (err) { console.warn('save failed', err); }
    setSaving(false);
    setStep(5);
  }

  async function startGmailConnect() {
    setSaving(true);
    try { await persistSettings(); } catch (err) { console.warn('save failed', err); }
    sessionStorage.setItem('ra_onboarding_resume', 'gmail');
    window.location.href = gmailConnectUrl();
  }

  async function finish() {
    setSaving(true);
    try { await persistSettings(); } catch (err) { console.warn('save failed', err); }
    sessionStorage.removeItem('ra_onboarding_resume');
    setSaving(false);
    onComplete();
  }

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: T.paper,
          color: T.graphiteDim,
          fontSize: 14,
        }}
      >
        Loading your accounts…
      </div>
    );
  }

  const providerLabel = provider === 'myob' ? 'MYOB' : 'Xero';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: T.paper }}>
      <div style={{ width: '100%', maxWidth: 480, background: '#FFFFFF', border: `1px solid ${T.paperLine}`, borderRadius: 16, padding: 40 }}>
        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 32 }}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              style={{
                height: 4,
                flex: 1,
                borderRadius: 2,
                background: i <= step ? T.navy : T.paperLine,
                transition: 'background 300ms ease',
              }}
            />
          ))}
        </div>

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <Wordmark size={32} color={T.graphite} />
            </div>
            <h1 style={{ ...sx.serif, fontSize: 28, color: T.graphite, marginBottom: 8 }}>Welcome</h1>
            <p style={{ fontSize: 14, color: T.graphiteDim, lineHeight: 1.6, marginBottom: 24 }}>
              Let's get you set up in 30 seconds. We need to know which accounts to use when posting receipts.
            </p>
            <div style={{ background: T.paper, borderRadius: 12, padding: 20, marginBottom: 28, fontSize: 14, color: T.graphiteMuted, lineHeight: 1.6 }}>
              <p style={{ marginBottom: 12, fontWeight: 500, color: T.graphite }}>How it works:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <span style={{ ...sx.mono, fontSize: 12, color: T.sage, fontWeight: 600, flexShrink: 0 }}>01</span>
                  <span>Forward receipt emails to your inbox address</span>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <span style={{ ...sx.mono, fontSize: 12, color: T.sage, fontWeight: 600, flexShrink: 0 }}>02</span>
                  <span>Our AI reads the receipt and extracts the details</span>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <span style={{ ...sx.mono, fontSize: 12, color: T.sage, fontWeight: 600, flexShrink: 0 }}>03</span>
                  <span>It picks the best expense category and posts to your books</span>
                </div>
              </div>
            </div>
            <Button variant="navy" size="large" onClick={() => setStep(1)} style={{ width: '100%' }}>
              Get started <Arrow size={16} />
            </Button>
          </div>
        )}

        {/* Step 1: Provider picker */}
        {step === 1 && (
          <div>
            <h2 style={{ ...sx.serif, fontSize: 24, color: T.graphite, marginBottom: 8 }}>Choose your books</h2>
            <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 24, lineHeight: 1.5 }}>
              Which accounting platform should we post receipts into?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {/* Xero tile */}
              <div
                style={{
                  border: `1px solid ${provider === 'xero' ? T.sageDarker : T.paperLine}`,
                  borderRadius: 12,
                  padding: 16,
                  background: provider === 'xero' ? T.paper : '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <XeroX size={32} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: T.graphite }}>Xero</div>
                  <div style={{ fontSize: 12, color: T.graphiteDim }}>
                    {provider === 'xero' ? 'Connected via signup' : 'Signed up with Xero'}
                  </div>
                </div>
                {provider === 'xero' && <Check size={18} color={T.sageDarker} />}
              </div>

              {/* MYOB tile */}
              <div
                style={{
                  border: `1px solid ${provider === 'myob' ? T.sageDarker : T.paperLine}`,
                  borderRadius: 12,
                  padding: 16,
                  background: provider === 'myob' ? T.paper : '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#6B2C91', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
                  M
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: T.graphite }}>MYOB</div>
                  <div style={{ fontSize: 12, color: T.graphiteDim }}>
                    {myobConnected
                      ? 'Connected'
                      : myobConfigured
                        ? 'Switch from Xero — disconnects Xero'
                        : 'Coming soon'}
                  </div>
                </div>
                {provider === 'myob' && <Check size={18} color={T.sageDarker} />}
                {provider !== 'myob' && myobConfigured && !myobConnected && (
                  <Button variant="ghostLight" size="small" onClick={switchToMyob} disabled={saving}>
                    {saving ? '…' : 'Connect'}
                  </Button>
                )}
              </div>

              {/* QuickBooks tile (soon) */}
              <div
                style={{
                  border: `1px solid ${T.paperLine}`,
                  borderRadius: 12,
                  padding: 16,
                  background: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  opacity: 0.6,
                }}
              >
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#2CA01C', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
                  Q
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: T.graphite }}>QuickBooks</div>
                  <div style={{ fontSize: 12, color: T.graphiteDim }}>Coming soon</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="ghostLight" onClick={() => setStep(0)} style={{ flex: 1 }}>
                Back
              </Button>
              <Button variant="navy" onClick={() => setStep(2)} style={{ flex: 1 }}>
                Next <Arrow size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Bank account */}
        {step === 2 && (
          <div>
            <h2 style={{ ...sx.serif, fontSize: 24, color: T.graphite, marginBottom: 8 }}>Bank account</h2>
            <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 24, lineHeight: 1.5 }}>
              Which bank account in {providerLabel} should receipts be posted against?
            </p>
            <FieldSelect
              label="Bank account"
              value={selectedBank}
              onChange={setSelectedBank}
              options={bankAccounts.map((a) => ({ value: a.id, label: a.name }))}
              placeholder="-- Select bank account --"
              style={{ marginBottom: 28 }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="ghostLight" onClick={() => setStep(1)} style={{ flex: 1 }}>
                Back
              </Button>
              <Button variant="navy" onClick={() => setStep(3)} disabled={!selectedBank} style={{ flex: 1 }}>
                Next <Arrow size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Expense account + (MYOB only) tax code */}
        {step === 3 && (
          <div>
            <h2 style={{ ...sx.serif, fontSize: 24, color: T.graphite, marginBottom: 8 }}>Default expense account</h2>
            <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 24, lineHeight: 1.5 }}>
              Our AI picks the best expense category per receipt. Pick a fallback account for the rare cases where nothing else fits.
            </p>
            <FieldSelect
              label="Expense account"
              value={provider === 'myob' ? selectedExpense : selectedExpense}
              onChange={setSelectedExpense}
              options={expenseAccounts.map((a) => ({ value: provider === 'myob' ? a.id : a.code, label: a.name }))}
              placeholder="-- Select expense account --"
              style={{ marginBottom: provider === 'myob' ? 16 : 28 }}
            />
            {provider === 'myob' && (
              <>
                <p style={{ fontSize: 13, color: T.graphiteDim, marginBottom: 10, lineHeight: 1.5 }}>
                  MYOB requires a tax code per line item. Pick your default (most Australian businesses use GST).
                </p>
                <FieldSelect
                  label="Tax code"
                  value={selectedTax}
                  onChange={setSelectedTax}
                  options={taxCodes.map((t) => ({ value: t.id, label: t.name }))}
                  placeholder="-- Select tax code --"
                  style={{ marginBottom: 28 }}
                />
              </>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="ghostLight" onClick={() => setStep(2)} style={{ flex: 1 }}>
                Back
              </Button>
              <Button
                variant="navy"
                onClick={() => setStep(4)}
                disabled={!selectedExpense || (provider === 'myob' && !selectedTax)}
                style={{ flex: 1 }}
              >
                Next <Arrow size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Sender emails */}
        {step === 4 && (
          <div>
            <h2 style={{ ...sx.serif, fontSize: 24, color: T.graphite, marginBottom: 8 }}>Sender emails</h2>
            <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 24, lineHeight: 1.5 }}>
              We identify you by the email you forward receipts from. Your login email is already registered. Add any other addresses you might forward from.
            </p>

            {aliases.length > 0 && (
              <div style={{ background: T.paper, border: `1px solid ${T.paperLine}`, borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
                {aliases.map((email, i) => (
                  <div
                    key={email}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 16px',
                      borderTop: i > 0 ? `1px solid ${T.paperLine}` : 'none',
                    }}
                  >
                    <span style={{ fontSize: 14, color: T.graphite }}>{email}</span>
                    <button
                      onClick={() => setAliases(aliases.filter((a) => a !== email))}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: T.graphiteDim, padding: 4 }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              <FieldInput
                label=""
                value={aliasInput}
                onChange={setAliasInput}
                placeholder="e.g. matt@otherdomain.com"
                type="email"
                style={{ flex: 1 }}
              />
              <Button variant="ghostLight" size="small" onClick={addAlias} style={{ alignSelf: 'flex-end', marginBottom: 1 }}>
                <Plus size={14} /> Add
              </Button>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="ghostLight" onClick={() => setStep(3)} style={{ flex: 1 }}>
                Back
              </Button>
              <Button variant="navy" onClick={gotoGmailStep} disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Saving...' : 'Next'} {!saving && <Arrow size={16} />}
              </Button>
            </div>

            <p style={{ fontSize: 12, color: T.graphiteDim, textAlign: 'center', marginTop: 16 }}>
              Optional. You can add more later in Settings.
            </p>
          </div>
        )}

        {/* Step 5: Gmail auto-sync */}
        {step === 5 && (
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 100, background: 'rgba(168,199,158,0.2)', color: T.sageDarker, fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14 }}>
              <Sparkle size={11} /> Recommended
            </div>
            <h2 style={{ ...sx.serif, fontSize: 24, color: T.graphite, marginBottom: 8 }}>Auto-scan your inbox</h2>
            <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 20, lineHeight: 1.55 }}>
              Connect Gmail once. Every hour we scan your inbox for receipts, invoices and order confirmations and pull them in automatically. You don't have to forward anything.
            </p>

            {gmail?.connected ? (
              <div style={{ background: T.paper, border: `1px solid ${T.sageDarker}`, borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.sage, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Check size={14} color={T.navy} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.graphite }}>Gmail connected</div>
                  <div style={{ fontSize: 12, color: T.graphiteDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{gmail.email}</div>
                </div>
              </div>
            ) : (
              <>
                <div style={{ background: T.paper, borderRadius: 12, padding: 16, marginBottom: 20, fontSize: 13, color: T.graphiteMuted, lineHeight: 1.6 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      'Read-only access — we never send or modify email',
                      'We only open messages that look like receipts',
                      'Counts towards your 20 free receipts — try it risk-free',
                      'Revoke any time in Settings or your Google account',
                    ].map((t, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <Check size={12} color={T.sageDarker} />
                        <span>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button variant="navy" size="large" onClick={startGmailConnect} disabled={saving} style={{ width: '100%', marginBottom: 12 }}>
                  {saving ? 'Preparing...' : 'Connect Gmail'}
                </Button>
              </>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="ghostLight" onClick={() => setStep(4)} style={{ flex: 1 }}>
                Back
              </Button>
              <Button variant="ghostLight" onClick={() => setStep(6)} style={{ flex: 1 }}>
                {gmail?.connected ? 'Next' : 'Skip for now'} <Arrow size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 6: Plans summary */}
        {step === 6 && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: T.sage, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <Check size={22} color={T.navy} />
              </div>
              <h1 style={{ ...sx.serif, fontSize: 30, color: T.graphite, marginBottom: 8, lineHeight: 1.1 }}>
                You're all set.
              </h1>
              <p style={{ fontSize: 14, color: T.graphiteDim, lineHeight: 1.6 }}>
                You're on the free plan — <strong style={{ color: T.graphite }}>20 receipts on us</strong>, no credit card required. Here's what you get today and what's waiting when you need more.
              </p>
            </div>

            <div style={{ background: T.paper, border: `1px solid ${T.sageDarker}`, borderRadius: 12, padding: 18, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ padding: '3px 9px', borderRadius: 100, background: T.sage, color: T.navy, fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>You're here</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: T.graphite }}>Free · $0</span>
              </div>
              <div style={{ fontSize: 13, color: T.graphiteMuted, lineHeight: 1.6 }}>
                20 receipts lifetime · AI extraction · {providerLabel} posting · Email forwarding · Upload & photo · <strong style={{ color: T.sageDarker }}>Gmail auto-sync</strong>
              </div>
            </div>

            {[
              { name: 'Solo', price: '$9.99/mo', receipts: '75/mo', feat: 'Everything in Free + monthly allowance + category rules' },
              { name: 'Business', price: '$19.99/mo', receipts: '300/mo', feat: 'Everything in Solo + priority support', popular: true },
              { name: 'Pro', price: '$39.99/mo', receipts: '1000/mo', feat: 'Everything in Business + multiple company files + priority chat' },
            ].map((p) => (
              <div key={p.name} style={{ background: '#FFFFFF', border: `1px solid ${T.paperLine}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: T.graphite }}>{p.name}</span>
                    {p.popular && <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: T.sage, color: T.navy, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Most popular</span>}
                  </div>
                  <div style={{ ...sx.mono, fontSize: 13, color: T.graphite, fontWeight: 600 }}>{p.price}</div>
                </div>
                <div style={{ fontSize: 12, color: T.graphiteMuted, lineHeight: 1.5 }}>
                  <strong style={{ color: T.graphite }}>{p.receipts}</strong> · {p.feat}
                </div>
              </div>
            ))}

            <p style={{ fontSize: 11, color: T.graphiteDim, textAlign: 'center', margin: '14px 0 18px', lineHeight: 1.5 }}>
              Upgrade any time from Settings. Overage billed at $0.15/receipt ($0.10 on Pro).
            </p>

            <Button variant="sage" size="large" onClick={finish} disabled={saving} style={{ width: '100%' }}>
              {saving ? 'Saving...' : 'Go to dashboard'}
              {!saving && <Arrow size={16} />}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
